<template>
    <div class="deals-page bg-background">

                <FullPageLoader :visible="isLoading" />

        <!-- HERO -->
        <v-container fluid class="hero-section py-16 text-center">
            <h1 class="hero-title">
                Bookings
            </h1>
        </v-container>

        <v-container fluid class="py-10">

            <v-container max-width="1100">
                <!-- Tabs: Upcoming / Past & canceled -->
                <v-tabs v-model="activeTab" density="comfortable" color="black" align-tabs="start"
                    class="bookings-tabs mb-4">
                    <v-tab value="upcoming">
                        Upcoming ({{ upcomingCount }})
                    </v-tab>
                    <v-tab value="past">
                        Past &amp; canceled ({{ pastCount }})
                    </v-tab>
                </v-tabs>

                <!-- Content -->
                <!-- Loading -->
                <v-row v-if="isLoading" class="mt-6" dense>
                    <v-col v-for="n in itemsPerPage" :key="n" cols="12">
                        <v-skeleton-loader type="image, text, text" class="booking-skeleton" elevation="4"
                            rounded="xl" />
                    </v-col>
                </v-row>

                <!-- Empty state -->
                <div v-else-if="totalCount === 0" class="empty-wrap">
                    <div class="empty-state text-center">
                        <v-avatar class="empty-avatar mb-4" size="88">
                            <v-icon size="40">mdi-ticket-confirmation-outline</v-icon>
                        </v-avatar>

                        <h2 class="text-h6 font-weight-semibold mb-2">
                            {{ activeTab === 'upcoming' ? 'No trips booked…yet!' : 'No trips here yet' }}
                        </h2>

                        <p class="text-body-2 text-medium-emphasis mb-6">
                            {{
                                activeTab === 'upcoming'
                                    ? 'Start planning your next cruise or getaway.'
                                    : 'Finished trips and canceled bookings will show up here.'
                            }}
                        </p>

                        <v-btn color="primary" size="large" class="px-6" to="/deals">
                            Book Your Next Adventure
                        </v-btn>
                    </div>
                </div>

                <!-- Cards -->
                <v-row v-else class="mt-6" dense>
                    <v-col v-for="card in pagedBookings" :key="card.id">
                        <BookedCruiseCard :booking="card" />
                    </v-col>
                </v-row>

                <!-- Pagination (only when there are items) -->
                <v-row v-if="totalCount > 0" class="mt-4" align="center" justify="space-between">
                    <v-col cols="auto">
                        <small class="text-medium-emphasis">
                            Showing {{ pageStart + 1 }}–{{ pageEnd }} of {{ totalCount }}
                        </small>
                    </v-col>

                    <v-col cols="auto" class="d-flex align-center">
                        <v-pagination v-model="page" :length="totalPages" :total-visible="7" density="comfortable"
                            prev-icon="mdi-chevron-left" next-icon="mdi-chevron-right" />
                    </v-col>
                </v-row>
            </v-container>
        </v-container>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, onActivated } from 'vue'
import { useStore } from 'vuex'
import { supabase } from '@/supabaseClient'
import BookedCruiseCard from '@/components/BookedCruiseCard.vue'
import FullPageLoader from '@/components/FullPageLoader.vue'

const store = useStore()
const user = computed(() => store.state.user)

const bookings = ref([])
const isLoading = ref(true)

// tabs + pagination
const activeTab = ref('upcoming') // 'upcoming' | 'past'
const page = ref(1)
const itemsPerPage = ref(3)

// uid tracking
const uid = ref(null)

// --- Helpers to classify bookings ---

// pick whichever date field you actually have on the booking row
function getBookingDate(b) {
    // tweak these field names to match your schema
    return (
        b.sail_date ||
        b.departure_date ||
        b.start_date ||
        b.check_in_date ||
        b.created_at
    )
}

const todayMidnight = () => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
}

function isCanceled(b) {
    const s = (b.status || '').toLowerCase()
    return s === 'canceled' || s === 'cancelled'
}

const upcomingBookings = computed(() => {
    const today = todayMidnight()
    return bookings.value.filter((b) => {
        const dateStr = getBookingDate(b)
        const canceled = isCanceled(b)
        if (!dateStr) return !canceled
        const date = new Date(dateStr)
        return date >= today && !canceled
    })
})

const pastBookings = computed(() => {
    const today = todayMidnight()
    return bookings.value.filter((b) => {
        const dateStr = getBookingDate(b)
        const canceled = isCanceled(b)
        if (!dateStr) return canceled
        const date = new Date(dateStr)
        return date < today || canceled
    })
})

const upcomingCount = computed(() => upcomingBookings.value.length)
const pastCount = computed(() => pastBookings.value.length)

const currentList = computed(() =>
    activeTab.value === 'upcoming'
        ? upcomingBookings.value
        : pastBookings.value
)

// pagination based on current tab
const totalCount = computed(() => currentList.value.length)
const totalPages = computed(() =>
    Math.max(1, Math.ceil((totalCount.value || 0) / itemsPerPage.value))
)
const pageStart = computed(() => (page.value - 1) * itemsPerPage.value)
const pageEnd = computed(() =>
    Math.min(pageStart.value + itemsPerPage.value, totalCount.value || 0)
)
const pagedBookings = computed(() =>
    currentList.value.slice(pageStart.value, pageEnd.value)
)

// reset page when switching tabs
watch(activeTab, () => {
    page.value = 1
})

// --- Supabase fetch ---

async function fetchBookings() {
    if (!uid.value) { isLoading.value = false; return }

    isLoading.value = true
    try {
        const { data, error } = await supabase
            .from('bookings')
            .select('*')
            .eq('user_id', uid.value)
            .order('created_at', { ascending: false })

        if (error) throw error
        bookings.value = data || []

        console.log(bookings)

        // clamp page to new length
        if (page.value > totalPages.value) page.value = totalPages.value
    } catch (e) {
        console.error('Error fetching bookings:', e)
        bookings.value = []
    } finally {
        isLoading.value = false
    }
}

// Watch uid (login/logout / initial load)
watch(
    uid,
    (u) => {
        if (u) {
            fetchBookings()
        } else {
            isLoading.value = false
            bookings.value = []
            page.value = 1
        }
    },
    { immediate: true }
)

// Get session on mount
onMounted(async () => {
    const {
        data: { session }
    } = await supabase.auth.getSession()
    uid.value = session?.user?.id ?? user.value?.uid ?? null
})

// React to auth changes
const {
    data: { subscription }
} = supabase.auth.onAuthStateChange((_evt, session) => {
    uid.value = session?.user?.id ?? null
})

onUnmounted(() => subscription?.unsubscribe?.())

// If page is kept alive, refresh when re-activated
onActivated(() => {
    if (uid.value) fetchBookings()
})

// Keep page in range when page size changes
watch(itemsPerPage, () => {
    if (page.value > totalPages.value) page.value = totalPages.value
})
</script>

<style scoped>
.bookings-tabs :deep(.v-tab) {
    text-transform: none;
    font-weight: 600;
    font-size: 0.9rem;
    min-width: 0;
    padding-inline: 0;
    margin-right: 32px;
}

.bookings-tabs :deep(.v-tab__slider) {
    border-radius: 0;
}

/* Empty state centering similar to Airbnb screenshot */
.empty-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 260px;
}

.empty-state {
    max-width: 360px;
}

.empty-avatar {
    border-radius: 9999px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    background-color: #f7f7f7;
}
</style>
